import { useState, useEffect, useRef, forwardRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import * as math from 'mathjs';

import { Registry } from '../../FormBuilderLibrary';
import { stringToHTML } from '../utils';

import { TextField, FormHelperText, Tooltip, FormControl } from '@mui/material';

import { getElementIcon } from '../utils';

import StyledWrapper from './style';

const MUICalculator = forwardRef((props, ref) => {
	const { isFormBuilder, disabled } = props;
	const reduxFormAnswers = useSelector((state) => state?.form?.form);
	const [formulaPieces] = useState(props?.data?.calc_formula?.BE || []);
	const [allAnswers, setAllAnswers] = useState([]);
	const [totalValue, setTotalValue] = useState('');

	const operators = ['(', ')', '+', '-', '/', '*'];

	const normalizeNumericLikeValue = (raw) => {
		if (raw === null || raw === undefined) {
			return '';
		}
		if (typeof raw === 'number') {
			return Number.isFinite(raw) ? raw.toString() : '';
		}
		if (typeof raw !== 'string') {
			return String(raw);
		}
		const trimmed = raw.trim();
		if (trimmed === '') {
			return '';
		}
		const withoutGrouping = trimmed.replace(/,/g, '');
		if (!Number.isNaN(Number(withoutGrouping))) {
			return withoutGrouping;
		}
		let cleaned = withoutGrouping.replace(/[^0-9.\-+]/g, '');
		if (cleaned === '' || cleaned === '-' || cleaned === '.' || cleaned === '-.') {
			return raw;
		}
		if (cleaned.startsWith('-')) {
			cleaned = `-${cleaned.slice(1).replace(/-/g, '')}`;
		} else {
			cleaned = cleaned.replace(/-/g, '');
		}
		const parts = cleaned.split('.');
		if (parts.length > 2) {
			const integerPart = parts.shift();
			const decimalPart = parts.join('');
			cleaned = `${integerPart}.${decimalPart}`;
		}
		return Number.isNaN(Number(cleaned)) ? raw : cleaned;
	};

	const getAllAnswers = () => {
		let answers = [];

		reduxFormAnswers?.forEach((answer) => {
			if (answer) {
				answers = [...answers, ...answer];
			}
		});

		setAllAnswers(answers);
	};

	const getValue = (field_name) => {
		const answer = allAnswers?.find((e) => e?.name === field_name);
		if (!answer || answer.value === '') {
			return { value: undefined, fieldType: undefined };
		}

		const formElement = document.getElementsByName(field_name)?.[0];
		const fieldType = formElement?.dataset?.fieldtype;
		const datasetRawValue = formElement?.dataset?.rawvalue;

		if (fieldType === 'percentage') {
			if (typeof datasetRawValue === 'string' && datasetRawValue !== '') {
				const normalizedRaw = normalizeNumericLikeValue(datasetRawValue);
				if (normalizedRaw !== '') {
					return { value: normalizedRaw, fieldType };
				}
			}
			const displayValue = formElement?.value?.replace(/%/g, '');
			if (displayValue) {
				const normalizedDisplay = normalizeNumericLikeValue(displayValue);
				if (normalizedDisplay !== '') {
					return { value: normalizedDisplay, fieldType };
				}
			}
			const normalizedAnswerFallback = normalizeNumericLikeValue(answer.value);
			return { value: normalizedAnswerFallback, fieldType };
		}

		if (fieldType === 'currency') {
			if (formElement?.value) {
				const normalizedInput = normalizeNumericLikeValue(formElement.value);
				if (normalizedInput !== '') {
					return { value: normalizedInput, fieldType };
				}
			}
			if (typeof datasetRawValue === 'string' && datasetRawValue !== '') {
				const normalizedDataset = normalizeNumericLikeValue(datasetRawValue);
				if (normalizedDataset !== '') {
					if (normalizedDataset.includes('.')) {
						return { value: normalizedDataset, fieldType };
					}
					const numericCandidate = Number(normalizedDataset);
					if (!Number.isNaN(numericCandidate)) {
						const digitCount = normalizedDataset.replace('-', '').length;
						if (digitCount > 2) {
							return { value: (numericCandidate / 100).toString(), fieldType };
						}
						return { value: numericCandidate.toString(), fieldType };
					}
				}
			}
			const normalizedAnswerFallback = normalizeNumericLikeValue(answer.value);
			return { value: normalizedAnswerFallback, fieldType };
		}

		const normalized = normalizeNumericLikeValue(answer.value);
		return { value: normalized, fieldType };
	};

	const parsedLabel = useMemo(() => stringToHTML(props?.data?.label), [props?.data?.label]);
	const parsedHelperText = useMemo(
		() => stringToHTML(props?.data?.help_message),
		[props?.data?.help_message]
	);
	const [isOverflowing, setIsOverflowing] = useState(false);
	const labelRef = useRef(null);
	const labelStyle = disabled ? { margin: '0px', color: 'rgba(0, 0, 0, 1)' } : { margin: '0px' };

	useEffect(() => {
		props.updateStore();
	}, [totalValue]);

	useEffect(() => {
		getAllAnswers();
	}, [reduxFormAnswers]);

	useEffect(() => {
		let formulaString = '';
		let formulaIncludesCurrency = false;
		if (formulaPieces.length > 0) {
			formulaPieces.forEach((formulaPiece) => {
				const isOperatorPiece = operators.includes(formulaPiece);
				if (isOperatorPiece) {
					const operator = formulaPiece;
					formulaString += operator;
				} else {
					const { value: formInputValue, fieldType } = getValue(formulaPiece?.replace(',', ''));
					if (fieldType === 'currency') {
						formulaIncludesCurrency = true;
					}
					const safeValue =
						formInputValue === undefined || formInputValue === '' || formInputValue === null
							? '0'
							: formInputValue;
					formulaString += safeValue;
				}
			});
			if (formulaString) {
				try {
					const evaluatedFormulaExpression = math.evaluate(formulaString);
					const numericResult = Number(evaluatedFormulaExpression);
					if (!Number.isFinite(numericResult)) {
						setTotalValue('');
						return;
					}
					const formattedResult = formulaIncludesCurrency
						? numericResult.toLocaleString(undefined, {
								minimumFractionDigits: 2,
								maximumFractionDigits: 2
						  })
						: numericResult.toLocaleString();
					setTotalValue(formattedResult);
				} catch (e) {
					setTotalValue('');
				}
			} else {
				setTotalValue('');
			}
		}
	}, [allAnswers]);

	useEffect(() => {
		// ARM-397. The form elements initially take up the entire width of the screen and then are resized to fit into the proper row size. This setTimeout adds a delay so we do this calculation after the form elements are set to their proper row width. Without this the scroll width and clientWidth will be the same on screens with a width of 2000px and above
		setTimeout(() => {
			if (labelRef.current) {
				setIsOverflowing(labelRef.current.scrollWidth > labelRef.current.clientWidth);
			}
		}, 100);
	});

	return isFormBuilder ? (
		<StyledWrapper.FormControlNoBorder sx={{ width: '100%' }} variant="outlined">
			<Tooltip
				arrow
				placement="top"
				title={isOverflowing ? `${props?.data?.label}` : ''}
				enterTouchDelay={0}
			>
				<StyledWrapper.CustomLabel>
					<StyledWrapper.LabelContents ref={labelRef}>
						{isFormBuilder && getElementIcon(props.data?.key)}
						{parsedLabel?.[0] ? parsedLabel : props?.data?.label}
						{isFormBuilder && (
							<span>
								{' '}
								{props.data?.unique_identifier
									? `(ff-${props.data?.unique_identifier})`
									: `(tmp-${props.data?.temp_order})`}
							</span>
						)}
					</StyledWrapper.LabelContents>
				</StyledWrapper.CustomLabel>
			</Tooltip>
			<TextField
				error={props?.data?.error}
				name={name}
				inputRef={ref}
				disabled={disabled}
				style={{ width: '100%' }}
				multiline
				InputLabelProps={{ shrink: isFormBuilder || undefined }}
			/>
			<StyledWrapper.FormHelperTextContainer>
				<FormHelperText error={props?.data?.error}>
					{props?.data?.error
						? props?.data?.error?.error
						: parsedHelperText?.[0]
						? parsedHelperText
						: props?.data?.help_message}
				</FormHelperText>
			</StyledWrapper.FormHelperTextContainer>
		</StyledWrapper.FormControlNoBorder>
	) : (
		<FormControl fullWidth>
			<Tooltip
				arrow
				placement="top"
				title={isOverflowing ? `${props?.data?.label}` : ''}
				enterTouchDelay={0}
			>
				<StyledWrapper.CustomLabel>
					<StyledWrapper.LabelContents ref={labelRef} style={labelStyle}>
						{parsedLabel?.[0] ? parsedLabel : props?.data?.label}
					</StyledWrapper.LabelContents>
				</StyledWrapper.CustomLabel>
			</Tooltip>
			<StyledWrapper.BorderOutline
				disabled={disabled}
				id={`${props?.data?.label}`}
				style={{ minHeight: '56px', marginTop: '16px' }}
			>
				<span style={labelStyle} ref={ref}>
					{totalValue}
				</span>
			</StyledWrapper.BorderOutline>
			<StyledWrapper.FormHelperTextContainer>
				<FormHelperText error={props?.data?.error}>
					{props?.data?.error
						? props?.data?.error?.error
						: parsedHelperText?.[0]
						? parsedHelperText
						: props?.data?.help_message}
				</FormHelperText>
			</StyledWrapper.FormHelperTextContainer>
		</FormControl>
	);
});

MUICalculator.displayName = 'MUI_Calculator';
Registry.register('MUI_Calculator', MUICalculator);

export default MUICalculator;
